/**
 * MarketplaceDetailModal — modeled on the GPT marketplace detail modal.
 *
 * Layout (top-down):
 *   - Big icon
 *   - Name + author + (optional) official badge
 *   - Tagline / description
 *   - Stats row: rating + install count
 *   - Capabilities (checkmarked bullet list)
 *   - Conversation Starters (chips)
 *   - Ratings histogram (5-star bars)
 *   - "More by <author>" — optional, only when present (we don't fetch
 *     these here in the first cut to keep the modal a single round-trip;
 *     can be added behind a useEffect later)
 *   - Fixed bottom CTA "Deploy to <projectName>"
 *
 * Accessibility:
 *   - role=dialog + aria-modal=true
 *   - focus trapped between the close button and the deploy button
 *   - Esc closes
 *   - Background click closes (with stopPropagation on the modal body)
 */

import React, { useEffect, useRef, useState } from 'react';
import Marketplace from '../../services/marketplace';
import {
  connectedProviders as computeConnectedProviders,
  requiredConnectedProviders as computeRequiredConnected,
  toggleableSkills as computeToggleableSkills,
  isLocked as computeIsLocked,
} from '../../utils/enabledIntegrations';
import { IonIcon } from '@ionic/react';
import {
  closeOutline,
  checkmarkCircle,
  star,
  starOutline,
  cloudUploadOutline,
  exitOutline,
  chevronDownOutline,
  chevronUpOutline,
} from 'ionicons/icons';
import MarketplaceWorkflowDeployFooter from './MarketplaceWorkflowDeployFooter';
import IntegrationRow from '../../components/shared/IntegrationsConnectList/IntegrationRow';
import PipelineGraphView from '../../components/shared/PipelineGraphView/PipelineGraphView';
import DeployInputsSection from '../../components/shared/SchemaForm/DeployInputsSection';
import {
  collectDeployInputFields,
  validateDeployInputs,
  buildDefaultInput,
} from '../../components/shared/SchemaForm/deployInputSources';
import useIntegrationConnect from '../../hooks/useIntegrationConnect';
import './MarketplaceDetailModal.css';
import './IntegrationsCollapse.css';

const MarketplaceDetailModal = ({
  entry,
  projectId,
  projectName,
  projectAiAgent = '',
  projectAiModel = '',
  isOpen,
  onClose,
  onDeploy,
  deploying,
  // One-Copilot-per-account gate (UX only; backend is the real guard). When
  // true, the Deploy button is disabled and `copilotBlockedMessage` is shown
  // beside it. Set by the parent when this is a Copilot template AND the
  // account already has one deployed.
  copilotBlocked = false,
  copilotBlockedMessage = '',
  // Click handler invoked when the user picks a star count, signature:
  //   onRate(entry, n) — n is 1..5. Should POST the rating and resolve
  //   to the refreshed entry so the modal can update its histogram.
  //   When omitted, the stars stay non-interactive (display-only).
  onRate,
}) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Integration status — fetched lazily on open so we only spend the
  // round-trip when the modal is actually visible. null = not loaded
  // yet; { required:[], ... } = loaded; error = string when fetch failed.
  // Loading flag drives the skeleton.
  const [intStatus, setIntStatus] = useState(null);
  const [intLoading, setIntLoading] = useState(false);
  const [intError, setIntError] = useState(null);

  // Collapse state for the Integrations grid. By default we show only the
  // first 8 rows (4 rows of the 2-column grid) and hide the rest behind a
  // "Show N more" chevron toggle. Only matters when there are >8 rows; ≤8
  // renders the full list with no toggle. Reset to collapsed each time the
  // modal opens or the viewed template changes so a previously-expanded
  // template doesn't leak into the next one.
  const [intExpanded, setIntExpanded] = useState(false);

  // Per-agent integration allowlist (least-privilege). The set of CONNECTED
  // provider ids this agent will be allowed to use once deployed. Defaults to
  // ALL connected selected (deselectable); required-connected providers are
  // LOCKED on (deselecting one would break the agent — the server force-adds
  // them regardless). When the user deselects nothing it's equivalent to "all
  // connected", so we only send a non-trivial allowlist on deploy. Re-derived
  // whenever the integration status loads (a Connect can add a new provider).
  const [enabledSel, setEnabledSel] = useState(() => new Set());
  const toggleEnabled = (id) => {
    setEnabledSel((prev) => {
      // Selecting is always allowed; deselecting is blocked when locked — a
      // hard-required provider OR the last member holding a required OR-group
      // (so you can swap GitHub↔GitLab, but not strand the requirement). Same
      // schema-driven rule the deployed-agent settings page uses.
      if (prev.has(id) && computeIsLocked(intStatus, prev, id)) return prev;
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Adaptive deploy-time required inputs (registry-driven) ───────
  // A template can declare deploy-time inputs via `deployInput` annotations
  // on its inputSchema (surfaced as entry.inputSchema). We collect the
  // user's entered values here and gate the deploy on the required ones.
  // `deployInputErrors` is set on a Deploy click (same one-click-surfaces-
  // -everything pattern the integration gate uses). Reset on open / switch.
  const deployInputFields = React.useMemo(
    () => collectDeployInputFields(entry?.inputSchema),
    [entry?.inputSchema],
  );
  const [deployInputValues, setDeployInputValues] = useState({});
  const [deployInputErrors, setDeployInputErrors] = useState({});
  const deployInputsSectionRef = useRef(null);
  useEffect(() => {
    // Fresh slate each time the modal opens or the viewed template changes,
    // so a previous template's entered values never leak into the next.
    setDeployInputValues({});
    setDeployInputErrors({});
  }, [isOpen, entry?.slug]);
  const updateDeployInput = (name, val) => {
    setDeployInputValues((prev) => ({ ...prev, [name]: val }));
    // Clear this field's error the moment the user edits it.
    setDeployInputErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // In-place error banner for a failed Connect. The shared connect engine
  // calls onError when an OAuth-start route fails (e.g. Notion's
  // /notion/oauth/start 404/500) or an unknown provider is clicked — we
  // surface it here so Connect is NEVER a silent no-op (was the Notion bug:
  // the error used to be console-logged only). Cleared when the user
  // re-tries or the modal closes.
  const [connectError, setConnectError] = useState(null);

  // Shared connect engine — the SAME one /integrations and the pipeline
  // Settings tab use. It owns every provider's in-place connect UI (OAuth
  // popups + GitLab/Lark/Figma/Linear/Plane/admin-key modals), the
  // postMessage listener that catches the OAuth popup's completion, and the
  // `refreshKey` that bumps after any successful Connect so our
  // integration-status fetch re-runs. No provider triggers a full-page
  // redirect anymore — everything is in-place.
  const { refreshKey, handleConnectClick, connectModals } = useIntegrationConnect({
    onError: (providerId, err) => {
      setConnectError(
        err?.message
          ? `Couldn’t start the connect flow: ${err.message}`
          : 'Couldn’t start the connect flow. Please try again.',
      );
    },
  });

  useEffect(() => {
    if (!isOpen || !entry?.slug || !projectId) {
      setIntStatus(null);
      setIntError(null);
      return undefined;
    }
    let cancelled = false;
    // Only show the loading skeleton on the FIRST fetch (when we
    // don't have any data yet). Subsequent refreshes (refreshKey bump
    // after a successful Connect) silently update in the background
    // — React's keyed-list diff updates only the changed row so the
    // user sees "Jira: Connect" → "Jira: Connected" cleanly, with
    // GitHub + GitLab rows untouched. Without this gate the whole
    // section would skeleton-flash on every Connect.
    if (refreshKey === 0) {
      setIntLoading(true);
    }
    setIntError(null);
    Marketplace.getIntegrationStatus(entry.slug, projectId)
      .then((res) => {
        if (cancelled) return;
        setIntStatus(res || null);
      })
      .catch((err) => {
        if (cancelled) return;
        // Graceful degrade — modal stays usable, deploy stays clickable.
        // The checklist section just won't render.
        setIntError(err?.message || 'Failed to load integration status');
      })
      .finally(() => {
        if (!cancelled) setIntLoading(false);
      });
    return () => { cancelled = true; };
  // refreshKey included so a successful Connect (OAuth or form modal)
  // forces a refetch without the skeleton flash (handled above).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entry?.slug, projectId, refreshKey]);

  // Seed the allowlist selection ONCE per modal-open. A fresh deploy defaults to
  // REQUIRED-connected only (least-privilege — optional integrations are opt-IN:
  // the user ticks the ones THIS agent should use; nothing is preselected just
  // because it's connected account-wide). Seeding once (keyed on the open + the
  // template), NOT on every intStatus change, means a later Connect or status
  // re-fetch never clobbers the user's ticks (that was the "untick does nothing"
  // bug — the [intStatus] effect kept resetting the set).
  const seededSlugRef = useRef(null);
  useEffect(() => {
    if (!isOpen) { seededSlugRef.current = null; return; }
    if (!intStatus) return;
    if (seededSlugRef.current === entry?.slug) return;
    seededSlugRef.current = entry?.slug;
    // Required-connected integrations are locked ON. No-connection toggleable
    // skills (codebase-memory etc) default ON too — they need no token, run
    // locally on the agent image, and the platform ships them enabled by
    // default (the user opts OUT, not in). Optional integrations stay opt-IN
    // (not preselected). So the initial selection = required-connected ∪
    // every toggleable skill id.
    const seed = computeRequiredConnected(intStatus);
    for (const s of computeToggleableSkills(intStatus)) seed.add(s.id);
    setEnabledSel(seed);
  }, [isOpen, entry?.slug, intStatus]);

  // The OAuth popup-done refresh (postMessage listener) and the
  // per-provider Connect dispatch now live in useIntegrationConnect — see
  // the hook call above. A successful Connect bumps `refreshKey`, which the
  // integration-status effect depends on, so the checklist re-fetches.

  // Clear any stale Connect error whenever the modal opens or closes so a
  // failed attempt on a previous open doesn't bleed into the next one.
  useEffect(() => {
    setConnectError(null);
    // Collapse the integrations grid back to 4 rows on every open/close +
    // template switch so the modal always starts in its default state.
    setIntExpanded(false);
  }, [isOpen, entry?.slug]);

  // ── Esc to close + focus trap ────────────────────────────────────
  // Lightweight focus trap: Tab/Shift-Tab cycles between the first
  // and last focusable element inside the dialog. Avoids pulling in a
  // dedicated trap-focus library; the focusable set inside this modal
  // is small enough (close button, chips for keyboard activation,
  // deploy button) that this hand-rolled cycle is fine.
  useEffect(() => {
    if (!isOpen) return undefined;
    const node = dialogRef.current;
    if (!node) return undefined;

    const previouslyFocused = document.activeElement;
    // Defer focus until paint so the open animation doesn't yank
    // attention from the user's last action.
    const t = setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 50);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = node.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  // v1/v2 public-mode click: parent's handleDeploy bounces to /connect
  // (unauth) or opens the wizard (authed). No name + warm-pool here;
  // the wizard collects those after the project is picked.
  const handlePublicDeploy = () => {
    if (deploying || !entry) return;
    if (copilotBlocked) return; // one-Copilot-per-account: hard stop in the UI
    // Surface BOTH gates on one click (no whack-a-mole): missing integrations
    // AND missing required deploy inputs. Each flags its own section; we abort
    // if either is unsatisfied.
    const inputsBad = flagDeployInputsThenAbort();
    if (blockedByMissing) flagMissingThenAbort();
    if (blockedByMissing || inputsBad) return;
    const defaultInput = buildDefaultInput(deployInputFields, deployInputValues);
    onDeploy?.(entry, defaultInput ? { defaultInput } : {});
  };

  // Deploy-gate state. When the user clicks Deploy with one or more
  // required integrations missing, we block the call and surface a
  // banner above the footer telling them exactly which integration(s)
  // to connect. The banner also flashes the integration section so the
  // user's eye lands there.
  const [blockReason, setBlockReason] = useState(null);
  const intSectionRef = useRef(null);
  const missingLabels = React.useMemo(() => {
    if (!intStatus || !Array.isArray(intStatus.missing)) return [];
    return intStatus.missing.map((entry) => {
      if (typeof entry === 'string') {
        return intStatus?.integrations?.[entry]?.name || entry;
      }
      const members = Array.isArray(entry?.any) ? entry.any : [];
      return members.map((id) => intStatus?.integrations?.[id]?.name || id).join(' or ');
    });
  }, [intStatus]);
  const blockedByMissing = missingLabels.length > 0;

  const flagMissingThenAbort = () => {
    setBlockReason(`Connect ${missingLabels.join(' and ')} before deploying.`);
    intSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  };

  // Deploy-input gate. Runs the registry's pre-flight required-check over
  // the collected values; on failure it flags the empty fields red and
  // scrolls the inputs section into view. Returns true when something is
  // missing (so the caller aborts). Derived fields are never checked here
  // (the backend resolves them). Runs on the SAME click as the integration
  // gate so one Deploy press surfaces every problem at once.
  const flagDeployInputsThenAbort = () => {
    const { ok, errors } = validateDeployInputs(deployInputFields, deployInputValues);
    if (ok) return false;
    setDeployInputErrors(errors);
    deployInputsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  };

  // v3 in-project deploy: delegate to MarketplaceWorkflowDeployFooter,
  // which owns the name field + validation + warm-pool menu. It
  // surfaces { workflowName, aiAgent, aiModel, warm } and we wrap with
  // the entry before passing to the parent's onDeploy.
  //
  // Validation gates (any one aborts the deploy but each independently
  // sets its own red highlight so the user sees EVERY problem from a
  // single click — no whack-a-mole):
  //   1. _agentMissing — footer flagged "no project default + no pick"
  //      → footer already turned its own select red
  //   2. blockedByMissing — required integration(s) not connected
  //      → we flash the integration rows red here
  const handleV3Deploy = (opts) => {
    const agentMissing = !!opts?._agentMissing;
    // Run every gate on this single click so the user sees ALL problems at
    // once: missing integrations, unpicked agent, AND missing required deploy
    // inputs. Each flags its own UI; we abort if any is unsatisfied.
    const inputsBad = flagDeployInputsThenAbort();
    if (blockedByMissing) flagMissingThenAbort();
    if (blockedByMissing || agentMissing || inputsBad) return undefined;
    // Strip the validation-helper flag before forwarding to the parent
    // so the deploy POST body stays clean.
    const { _agentMissing, ...rest } = opts || {};
    void _agentMissing;
    // Attach the per-agent integration allowlist. We send it ONLY when the user
    // actually deselected at least one connected integration (i.e. the selection
    // is a strict SUBSET of all connected). When everything connected is still
    // selected the allowlist is equivalent to the "all connected" default, so we
    // omit it — keeping the deploy body byte-identical to today's behaviour and
    // letting the backend treat absence as "all connected allowed". (Required
    // members are locked-on in the UI and force-added server-side anyway.)
    const allConnected = computeConnectedProviders(intStatus);
    const selected = [...enabledSel].filter((id) => allConnected.has(id));
    const isStrictSubset = allConnected.size > 0 && selected.length < allConnected.size;
    if (isStrictSubset) {
      rest.enabledIntegrations = selected;
    }
    // Adaptive deploy-time inputs (registry-driven). Collect the entered
    // values into a `defaultInput` object and attach it. Derived fields
    // (deriveFrom) are OMITTED — the backend resolves them at deploy. When
    // the template declares no deploy inputs, buildDefaultInput returns
    // undefined and the body stays byte-identical to today's behaviour.
    const defaultInput = buildDefaultInput(deployInputFields, deployInputValues);
    if (defaultInput) {
      rest.defaultInput = defaultInput;
    }
    // Return the deploy promise so the footer can await it and flip into
    // inline key-entry mode on a 428 AGENT_KEY_MISSING (without losing the
    // modal). Errors propagate; the footer/parent surface them.
    return onDeploy?.(entry, rest);
  };

  // Rating state — derived from the entry but ephemeral on hover so the
  // user can preview the star count before committing. `submittingRating`
  // disables clicks during the inflight POST.
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  if (!isOpen || !entry) return null;

  const histogram = entry.ratingHistogram || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const histogramTotal = [1, 2, 3, 4, 5].reduce((a, k) => a + (histogram[k] || 0), 0);
  const rating = Number(entry.ratingAvg) || 0;
  const myRatingValue = Number(entry.myRating?.rating) || 0;
  // Public-marketplace mode: no project context yet. The Deploy click is
  // a bounce — parent navigates to /connect (unauth) or /projects with
  // ?deployTemplate=<slug> (authed but no project picked). In this mode
  // the workflow-name input is meaningless (no namespace to validate
  // against) so we hide it and simplify the footer.
  const isPublic = !projectId;
  // Stars filled = hover (live preview) > my prior rating > rounded avg.
  // Showing "my rating" by default makes the affordance obvious (the
  // stars look "set") and matches App Store / GitHub patterns.
  const starsFilled = hoverRating || myRatingValue || Math.round(rating);
  const canRate = typeof onRate === 'function';

  return (
    <div className="mkt-modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="mkt-modal mkt-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mkt-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="mkt-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header">
            {entry.icon ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={entry.icon} alt="" className="mkt-modal-icon" />
            ) : (
              <span className="mkt-modal-icon mkt-modal-icon--fallback">
                {(entry.name || entry.slug || '?').slice(0, 1).toUpperCase()}
              </span>
            )}
            <h2 id="mkt-modal-title" className="mkt-modal-title">{entry.name || entry.slug}</h2>
            <p className="mkt-modal-author">
              By {entry.authorName || 'Unknown'}
              {entry.authorType === 'official' && (
                <span className="mkt-modal-author-badge">Official</span>
              )}
            </p>
            <p className="mkt-modal-tagline">
              {entry.description || entry.tagline}
            </p>

            <div className="mkt-modal-stats" aria-label="Stats">
              <div className="mkt-modal-stat">
                <div className="mkt-modal-stat-value">
                  <span className="mkt-modal-stat-number">{rating.toFixed(1)}</span>
                  {/* Stars: clickable when onRate is wired. The POST is
                      upsert by (workflowSlug, userId) on the backend, so
                      re-rating just replaces the prior row + recomputes
                      the histogram atomically. */}
                  <span
                    className={`mkt-modal-stat-stars ${canRate ? 'is-interactive' : ''}`}
                    role={canRate ? 'radiogroup' : undefined}
                    aria-label={canRate ? 'Rate this agent' : undefined}
                    onMouseLeave={canRate ? () => setHoverRating(0) : undefined}
                  >
                    {[1, 2, 3, 4, 5].map((n) => {
                      const filled = n <= starsFilled;
                      const StarButton = canRate ? 'button' : 'span';
                      const extraProps = canRate
                        ? {
                            type: 'button',
                            role: 'radio',
                            'aria-checked': n === myRatingValue,
                            'aria-label': `Rate ${n} star${n === 1 ? '' : 's'}`,
                            disabled: submittingRating,
                            onMouseEnter: () => setHoverRating(n),
                            onFocus: () => setHoverRating(n),
                            onBlur: () => setHoverRating(0),
                            onClick: async () => {
                              if (!entry || submittingRating) return;
                              setSubmittingRating(true);
                              try {
                                await onRate(entry, n);
                              } finally {
                                setSubmittingRating(false);
                              }
                            },
                          }
                        : {};
                      return (
                        <StarButton
                          key={n}
                          className={`mkt-modal-star ${filled ? 'is-filled' : ''}`}
                          {...extraProps}
                        >
                          <IonIcon icon={filled ? star : starOutline} aria-hidden="true" />
                        </StarButton>
                      );
                    })}
                  </span>
                </div>
                <div className="mkt-modal-stat-label">
                  {myRatingValue > 0
                    ? `You rated ${myRatingValue} · ${histogramTotal} ${histogramTotal === 1 ? 'rating' : 'ratings'}`
                    : `${histogramTotal} ${histogramTotal === 1 ? 'rating' : 'ratings'}`}
                </div>
              </div>
              {entry.category && (
                <div className="mkt-modal-stat">
                  <div className="mkt-modal-stat-value mkt-modal-stat-category">
                    {entry.category}
                  </div>
                  <div className="mkt-modal-stat-label">category</div>
                </div>
              )}
            </div>
          </header>

          {/* Required + Optional integrations.
              - Required: deploy gate; if any are missing → Deploy is blocked
                and `blockReason` flashes the unsatisfied rows.
              - Optional: shown only when present; "Connect (optional)" badge,
                no deploy gate. Typical case: `git` skill on a workflow that
                only clones public repos. Section is hidden entirely if the
                workflow has no optional dependencies.
              Both render the same row shape via renderIntegrationRow so
              the visual contract stays identical across the two sections. */}
          {(() => {
            // Per-agent allowlist (least-privilege). A CONNECTED integration row
            // DOUBLES AS A TOGGLE: the green check means "connected"; HIGHLIGHTING
            // THE WHOLE ROW means "enabled for this agent" once deployed. No
            // checkbox, no separate section. HARD-required providers are locked
            // ON (the agent depends on them); a required OR-group locks only its
            // LAST still-selected member (so GitHub↔GitLab is swappable, ≥1 must
            // stay). Selection is only meaningful for an in-project deploy; the
            // public/logged-out modal renders the connected row read-only.
            const allowSelectable = !isPublic; // selection only in-project
            // Logged-out / no-project (isPublic): Connect needs auth, so the click
            // bounces to login rather than starting an OAuth flow that 401s.
            const onConnect = (cid, cpath) => {
              if (isPublic) {
                window.location.assign(`/connect?returnTo=${encodeURIComponent('/marketplace')}`);
                return;
              }
              setConnectError(null);
              handleConnectClick(cid, cpath);
            };
            // One row via the SHARED <IntegrationRow> — identical to the deployed-
            // agent settings list. `flashed` (deploy gate) only applies to a
            // not-connected required row.
            const rowFor = (id, { connected, optional, flashed, key }) => {
              const meta = intStatus?.integrations?.[id] || { id, name: id, connectPath: '/integrations' };
              return (
                <IntegrationRow
                  key={key}
                  id={id}
                  name={meta.name}
                  connectPath={meta.connectPath}
                  connected={connected}
                  selectable={allowSelectable}
                  selected={allowSelectable && enabledSel.has(id)}
                  locked={allowSelectable && computeIsLocked(intStatus, enabledSel, id)}
                  optional={optional}
                  flashed={flashed}
                  onToggle={toggleEnabled}
                  onConnect={onConnect}
                />
              );
            };

            const renderIntegrationRow = (entry, connectedList, { optionalSection }) => {
              if (typeof entry === 'string') {
                const id = entry;
                const isConnected = (connectedList || []).includes(id);
                const flashed = !isConnected && !optionalSection && !!blockReason;
                return rowFor(id, { connected: isConnected, optional: optionalSection, flashed, key: id });
              }
              // OR group — expand into one row per member. Preferred display order:
              // Slack first (top-left), Lark second (top-right); others keep order.
              // (Backend alpha-sorts OR-group members; we override for display.)
              const MEMBER_ORDER = { slack: 0, lark: 1 };
              const rawMembers = Array.isArray(entry?.any) ? entry.any : [];
              if (rawMembers.length === 0) return null;
              // Match the connected group ORDER-INDEPENDENTLY (canonical sorted key
              // on both sides — display re-sort below must not break matching).
              const canonicalKey = (arr) => arr.slice().sort().join(',');
              const matchedGroup = (connectedList || []).find(
                (c) => typeof c === 'object' && Array.isArray(c?.any)
                  && canonicalKey(c.any) === canonicalKey(rawMembers),
              );
              const matched = matchedGroup?.connectedMembers || [];
              const groupSatisfied = matched.length > 0;
              const members = rawMembers
                .slice()
                .sort((a, b) => (MEMBER_ORDER[a] ?? 99) - (MEMBER_ORDER[b] ?? 99));
              return members.map((mid) => {
                const isThisConnected = matched.includes(mid);
                const flashed = !isThisConnected && !optionalSection && !groupSatisfied && !!blockReason;
                return rowFor(mid, {
                  connected: isThisConnected,
                  optional: optionalSection || groupSatisfied,
                  flashed,
                  key: `${members.join(',')}:${mid}`,
                });
              });
            };

            // The integration LIST is a PUBLIC property of the template
            // (entry.required/optionalIntegrations), so it renders even when
            // logged-out or when the auth-gated status fetch is empty/failed.
            // Connection STATUS (the green check) comes from intStatus when
            // available; when absent every row shows "Connect" (read-only), and
            // a logged-out visitor's Connect click bounces to login.
            const reqList = (intStatus && Array.isArray(intStatus.required) && intStatus.required.length)
              ? intStatus.required
              : (Array.isArray(entry?.requiredIntegrations) ? entry.requiredIntegrations : []);
            const optList = (intStatus && Array.isArray(intStatus.optional) && intStatus.optional.length)
              ? intStatus.optional
              : (Array.isArray(entry?.optionalIntegrations) ? entry.optionalIntegrations : []);
            // No-connection toggleable skills (codebase-memory etc) — these need
            // NO Connect step; render as toggle-only rows (default ON). Source:
            // the authed status feed when present (carries the same {id,name,
            // description} shape), else the public template row (entry.
            // toggleableSkills, stamped by the marketplace sync) so they show
            // even logged-out / before the status fetch resolves.
            const toggList = (intStatus && Array.isArray(intStatus.toggleableSkills) && intStatus.toggleableSkills.length)
              ? computeToggleableSkills(intStatus)
              : (Array.isArray(entry?.toggleableSkills)
                  ? entry.toggleableSkills.filter((s) => s && typeof s.id === 'string')
                  : []);
            const hasRequired = reqList.length > 0;
            const hasOptional = optList.length > 0;
            const hasToggleable = toggList.length > 0;
            const hasAny = hasRequired || hasOptional || hasToggleable;

            // One section, two flavors of row. Required entries render
            // "Connect"; optional render "Connect (optional)" — the badge
            // distinguishes them inline so we don't need a second header.
            // The Deploy gate is independent of this rendering — it reads
            // `missing` (only required) so optional rows never block.
            return (
              <section className="mkt-modal-section" ref={intSectionRef}>
                <div className="mkt-modal-section-title-row">
                  <h3 className="mkt-modal-section-title">Skills</h3>
                  <a
                    href="/integrations"
                    className="mkt-modal-section-link"
                    aria-label="Manage integrations"
                    title="Manage integrations"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IonIcon icon={exitOutline} aria-hidden="true" />
                  </a>
                </div>
                {intLoading ? (
                  // Skeleton mirrors the real grid: one boxed row per declared
                  // integration so the section reserves the SAME height (no
                  // layout jump when the status resolves). Counts come from the
                  // marketplace row's required+optional lists; fall back to a
                  // sensible default when absent.
                  (() => {
                    const skelCount = Math.max(
                      2,
                      (entry?.requiredIntegrations?.length || 0)
                        + (entry?.optionalIntegrations?.length || 0)
                      || 4,
                    );
                    return (
                      <ul className="mkt-modal-integrations" aria-busy="true" aria-label="Loading integrations">
                        {Array.from({ length: skelCount }).map((_, i) => (
                          <li key={i} className="mkt-modal-integration-row mkt-skel-row">
                            <span className="mkt-skel-dot" />
                            <span className="mkt-skel-stripe" />
                          </li>
                        ))}
                      </ul>
                    );
                  })()
                ) : hasAny ? (
                  // List first (from public entry, with intStatus status when
                  // present) — so an empty/failed status fetch never hides the
                  // template's declared integrations.
                  //
                  // Collapse: renderIntegrationRow returns either a single <li>
                  // or (for an OR-group) an ARRAY of <li> rows, so the true
                  // rendered-row count is only known after flattening. We build
                  // the full flat list, then — when there are >8 rows and the
                  // section is collapsed — slice to the first 8 (4 grid rows)
                  // and show a centered chevron toggle to reveal the rest.
                  (() => {
                    const allRows = [
                      ...(hasRequired
                        ? reqList.map((e) =>
                            renderIntegrationRow(e, intStatus?.connected, { optionalSection: false }))
                        : []),
                      ...(hasOptional
                        ? optList.map((e) =>
                            renderIntegrationRow(e, intStatus?.optionalConnected, { optionalSection: true }))
                        : []),
                      // No-connection toggleable skills (codebase-memory etc) —
                      // toggle-only rows via the SAME <IntegrationRow> in
                      // noIntegration mode the deployed-agent settings list uses.
                      // Selectable only in-project (the public/logged-out modal
                      // renders them read-only "On"); selection feeds the per-agent
                      // enabledIntegrations allowlist (default ON, toggled-off ⇒
                      // excluded). They have no Connect step + never gate Deploy.
                      ...(hasToggleable
                        ? toggList.map((s) => (
                            <IntegrationRow
                              key={`toggle:${s.id}`}
                              id={s.id}
                              name={s.name || s.id}
                              description={s.description || ''}
                              noIntegration
                              selectable={!isPublic}
                              selected={!isPublic && enabledSel.has(s.id)}
                              onToggle={toggleEnabled}
                            />
                          ))
                        : []),
                    ]
                      // Flatten OR-group arrays + drop nulls (empty groups) so
                      // we count + slice the ACTUAL <li> rows the user sees.
                      .flat()
                      .filter(Boolean);
                    const totalCount = allRows.length;
                    const COLLAPSED_COUNT = 8;
                    const collapsible = totalCount > COLLAPSED_COUNT;
                    const visibleRows = collapsible && !intExpanded
                      ? allRows.slice(0, COLLAPSED_COUNT)
                      : allRows;
                    const hiddenCount = totalCount - COLLAPSED_COUNT;
                    return (
                      <>
                        <ul className="mkt-modal-integrations">
                          {visibleRows}
                        </ul>
                        {collapsible && (
                          <button
                            type="button"
                            className="mkt-modal-integrations-toggle"
                            aria-expanded={intExpanded}
                            aria-label={
                              intExpanded
                                ? 'Show fewer integrations'
                                : `Show ${hiddenCount} more integration${hiddenCount === 1 ? '' : 's'}`
                            }
                            onClick={(e) => { e.stopPropagation(); setIntExpanded((v) => !v); }}
                          >
                            <span className="mkt-modal-integrations-toggle-label">
                              {intExpanded ? 'Show less' : `Show ${hiddenCount} more`}
                            </span>
                            <IonIcon
                              icon={intExpanded ? chevronUpOutline : chevronDownOutline}
                              aria-hidden="true"
                            />
                          </button>
                        )}
                      </>
                    );
                  })()
                ) : intError ? (
                  <p className="mkt-modal-integration-error">Couldn’t load integration status. Deploy will still work; you may need to connect integrations from Settings after install.</p>
                ) : (
                  <p className="mkt-modal-integration-none">None</p>
                )}
                {/* Connect-failure banner — surfaces an OAuth-start error
                    (e.g. Notion's /notion/oauth/start 404/500) so Connect
                    is never a silent no-op. Reuses the existing error
                    style. Cleared on the next Connect attempt + on close. */}
                {connectError && (
                  <p className="mkt-modal-integration-error" role="alert">{connectError}</p>
                )}
              </section>
            );
          })()}

          {/* Adaptive "Required inputs" section — registry-driven from the
              template's inputSchema deployInput annotations. Renders nothing
              for templates with no deploy inputs (backward compatible), so
              existing agents (sentry-triage, code-review) are unaffected.
              flaky-test-fixer gets a GitHub-repo picker (repoUrl) + a derived
              hint (circleProjectSlug). */}
          <DeployInputsSection
            inputSchema={entry.inputSchema}
            values={deployInputValues}
            onChange={updateDeployInput}
            errors={deployInputErrors}
            disabled={deploying}
            sectionRef={deployInputsSectionRef}
          />

          {Array.isArray(entry.capabilities) && entry.capabilities.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Capabilities</h3>
              <ul className="mkt-modal-capabilities">
                {entry.capabilities.map((cap, i) => (
                  <li key={i}>
                    <IonIcon icon={checkmarkCircle} aria-hidden="true" />
                    <span>{cap}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Pipeline graph — the full serialized graph (nodes + edges +
              nodeConfigs) the marketplace sync stamps onto the row, surfaced
              by the detail API as `entry.graph`. Rendered read-only via the
              shared PipelineGraphView. Only shown when graph data is present
              (older rows / templates without a serializable graph). */}
          {entry.graph && Array.isArray(entry.graph.nodes) && entry.graph.nodes.length > 0 && (
            <section className="mkt-modal-section">
              <PipelineGraphView graph={entry.graph} />
            </section>
          )}

        </div>

        <footer className="mkt-modal-footer">
          {/* One-Copilot-per-account: explain WHY Deploy is greyed out. Shown
              above the footer button(s) in both public + in-project modes. The
              backend independently returns 409 COPILOT_LIMIT_REACHED. */}
          {copilotBlocked && (
            <p className="mkt-modal-copilot-limit" role="status">
              {copilotBlockedMessage || 'You already have a Copilot deployed.'}
            </p>
          )}
          {isPublic ? (
            /* v1 (unauth) + v2 (authed, no project) share a single
               full-width Deploy. Parent decides what happens — bounce
               to /connect or open the wizard. Name + warm-pool live
               in the wizard / v3 footer (see below). */
            <button
              type="button"
              className={`mkt-modal-deploy mkt-modal-deploy--full${deploying ? ' is-loading' : ''}`}
              onClick={handlePublicDeploy}
              disabled={deploying || copilotBlocked}
              aria-label="Deploy"
              aria-busy={deploying}
              title={copilotBlocked ? (copilotBlockedMessage || undefined) : undefined}
            >
              <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
              Deploy
            </button>
          ) : (
            <MarketplaceWorkflowDeployFooter
              entry={entry}
              projectId={projectId}
              projectName={projectName}
              projectAiAgent={projectAiAgent}
              projectAiModel={projectAiModel}
              deploying={deploying}
              disabled={copilotBlocked}
              onDeploy={handleV3Deploy}
            />
          )}
        </footer>
      </div>

      {/*
        Every provider's in-place Connect UI (GitLab/Lark/Figma/Linear/Plane
        + the admin-key paste modals) — owned by useIntegrationConnect, the
        SAME engine /integrations and the pipeline Settings tab use. Rendered
        OUTSIDE the dialog div: IonModal portals to <body> so the DOM tree
        placement doesn't matter, but mounting them here keeps "things this
        modal owns" at one glance. Each fires onConnected after the
        integration row is created server-side, bumping refreshKey so the
        checklist re-fetches and the row flips to "Connected".
      */}
      {connectModals}
    </div>
  );
};

export default MarketplaceDetailModal;
